import { Goal, ID, Task, UpdateGoal } from '@griply/types';

import { insertDuplicatesAfterSelection } from './insert-duplicates-after-selection';

export type UpdateLinkedGoalOptions = {
  taskPairs: Array<{ oldTask: Task; newTask: Task }>;
  getGoalDocById: (id: ID) => Promise<Goal | undefined>;
  updateGoal: (goal: UpdateGoal) => Promise<void> | void;
};

export const updateLinkedGoal = async ({
  taskPairs,
  getGoalDocById,
  updateGoal,
}: UpdateLinkedGoalOptions): Promise<void> => {
  const goalGroups = new Map<ID, Array<{ oldTask: Task; newTask: Task }>>();
  taskPairs.forEach(({ oldTask, newTask }) => {
    if (oldTask.parentIds?.length) {
      return;
    }
    oldTask.goalIds.forEach((goalId) => {
      const existing = goalGroups.get(goalId) ?? [];
      goalGroups.set(goalId, [...existing, { oldTask, newTask }]);
    });
  });

  if (!goalGroups.size) {
    return;
  }

  await Promise.all(
    Array.from(goalGroups.entries()).map(async ([goalId, pairs]) => {
      const goal = await getGoalDocById(goalId);
      if (!goal || (!goal.taskSorting?.length && !goal.sections?.length)) {
        return;
      }

      // A sectioned goal carries both arrays: `taskSorting` backs the flat
      // (None) view, `sections[].tasks` the Section view. Both must receive the
      // duplicate or it falls into the "no section" bucket in whichever view
      // wasn't updated. taskCount/completedTaskCount are deltas applied via
      // increment() — keep this to a single updateGoal call so they fire once.
      const update: UpdateGoal = {
        id: goal.id,
        taskCount: pairs.length,
        completedTaskCount: pairs.filter(({ oldTask }) => !!oldTask.completedAt)
          .length,
      };

      if (goal.taskSorting?.length) {
        update.taskSorting = insertDuplicatesAfterSelection(
          goal.taskSorting,
          pairs,
        );
      }

      if (goal.sections?.length) {
        update.sections = goal.sections.map((section) => {
          const tasks = insertDuplicatesAfterSelection(
            section.tasks ?? [],
            pairs,
          );
          if (tasks.length === (section.tasks?.length ?? 0)) {
            return section;
          }
          return { ...section, tasks };
        });
      }

      await updateGoal(update);
    }),
  );
};
